import React, { useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock3, Inbox, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { subDays, startOfDay } from "date-fns";
import { LeadStageType } from "@/context/LeadContext";
import PartnerPageHeader from "@/components/partners/PartnerPageHeader";
import PartnerTimeFilter, { TimeRangeType } from "@/components/partners/PartnerTimeFilter";
import PartnerMetricCard from "@/components/partners/PartnerMetricCard";
import PartnerLeadCard from "@/components/partners/PartnerLeadCard";
import TodaysFocus from "@/components/partners/TodaysFocus";
import LeadDetailsModal from "@/components/partners/LeadDetailsModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { usePartnerLeads } from "@/hooks/usePartnerLeads";
import { PARTNER_STAGES, PartnerOutletContext, parsePortalDate } from "@/types/partnerPortal";

const rangeStart = (range: TimeRangeType) => {
  const now = new Date();
  if (range === "today") return startOfDay(now);
  if (range === "7d") return subDays(now, 7);
  if (range === "30d") return subDays(now, 30);
  if (range === "90d") return subDays(now, 90);
  return new Date(0);
};

const PartnerPipelinePage = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const { partner } = useOutletContext<PartnerOutletContext>();
  const { leads, isLoading, isFetching, error, refetch, updateStage, updatingLeadId, markViewed } = usePartnerLeads(partnerId);
  const [timeRange, setTimeRange] = useState<TimeRangeType>("all");
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [mobileStage, setMobileStage] = useState<LeadStageType>("Νέο");
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<LeadStageType | null>(null);

  const filteredLeads = useMemo(() => {
    const start = rangeStart(timeRange);
    return leads.filter((lead) => parsePortalDate(lead.createdAt) >= start);
  }, [leads, timeRange]);

  const stats = useMemo(() => {
    const total = filteredLeads.length;
    const fresh = filteredLeads.filter((lead) => lead.stage === "Νέο").length;
    const active = filteredLeads.filter((lead) => lead.stage === "σεΕκκρεμότητα" || lead.stage === "ήθελεΠληροφορίες").length;
    const closed = filteredLeads.filter((lead) => lead.stage === "έκλεισεΗΔουλειά").length;
    const unseen = filteredLeads.filter((lead) => !lead.linkOpened && lead.stage !== "έκλεισεΗΔουλειά").length;
    return { total, fresh, active, closed, unseen, closeRate: total ? Math.round((closed / total) * 100) : 0 };
  }, [filteredLeads]);

  const byStage = useMemo(() => new Map(
    PARTNER_STAGES.map((stage) => [stage.key, filteredLeads.filter((lead) => lead.stage === stage.key)]),
  ), [filteredLeads]);

  const handleDrop = (stage: LeadStageType) => {
    if (draggedLeadId) void updateStage(draggedLeadId, stage);
    setDraggedLeadId(null);
    setDragOverStage(null);
  };

  return (
    <div className="space-y-6">
      <LeadDetailsModal leadId={selectedLeadId} onClose={() => setSelectedLeadId(null)} partnerId={partnerId} onViewed={markViewed} />

      <PartnerPageHeader
        eyebrow="Η καθημερινή σας εικόνα"
        title={`Καλημέρα, ${partner.name.split(" ")[0]}`}
        description="Δείτε πρώτα όσα χρειάζονται ενέργεια και ενημερώστε το στάδιο μετά από κάθε επικοινωνία."
        actions={
          <Button variant="outline" onClick={() => void refetch()} disabled={isFetching} className="h-11 rounded-xl bg-white">
            <RefreshCw className={cn("mr-2 h-4 w-4", isFetching && "animate-spin")} /> Ανανέωση
          </Button>
        }
      />

      <div data-partner-tour="pipeline-summary" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <PartnerMetricCard label="Νέα leads" value={stats.fresh} hint={stats.unseen ? `${stats.unseen} δεν έχουν προβληθεί` : "Όλα έχουν προβληθεί"} icon={<Sparkles className="h-5 w-5" />} tone="violet" />
        <PartnerMetricCard label="Σε εξέλιξη" value={stats.active} hint="Εκκρεμή και πληροφορίες" icon={<Clock3 className="h-5 w-5" />} tone="amber" />
        <PartnerMetricCard label="Έκλεισαν" value={stats.closed} hint="Στην επιλεγμένη περίοδο" icon={<CheckCircle2 className="h-5 w-5" />} tone="emerald" />
        <PartnerMetricCard label="Ποσοστό κλεισίματος" value={`${stats.closeRate}%`} hint={`${stats.total} leads συνολικά`} icon={<Inbox className="h-5 w-5" />} tone="blue" />
      </div>

      <TodaysFocus leads={filteredLeads} partnerName={partner.name} onOpenLead={setSelectedLeadId} />

      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-950">Pipeline εργασιών</h2>
            <p className="mt-1 text-sm text-slate-500">{filteredLeads.length} leads στην επιλεγμένη περίοδο.</p>
          </div>
          <PartnerTimeFilter value={timeRange} onChange={setTimeRange} />
        </div>

        {error && (
          <div className="flex flex-col items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2 text-sm font-medium text-rose-800"><AlertCircle className="h-4 w-4" /> {error}</p>
            <Button variant="outline" onClick={() => void refetch()} className="h-10 rounded-xl border-rose-200 bg-white">Νέα προσπάθεια</Button>
          </div>
        )}

        {isLoading ? (
          <div className="grid gap-4 lg:grid-cols-4">
            {[0, 1, 2, 3].map((item) => <Skeleton key={item} className="h-72 rounded-2xl" />)}
          </div>
        ) : (
          <>
            <div className="-mx-4 overflow-x-auto px-4 md:hidden">
              <div className="flex min-w-max gap-2 pb-1">
                {PARTNER_STAGES.map((stage) => {
                  const count = byStage.get(stage.key)?.length ?? 0;
                  return (
                    <button
                      key={stage.key}
                      type="button"
                      onClick={() => setMobileStage(stage.key)}
                      className={cn(
                        "flex h-11 items-center gap-2 rounded-xl border px-3 text-xs font-bold",
                        mobileStage === stage.key ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-600",
                      )}
                    >
                      {stage.shortLabel}<span className={cn("rounded-full px-1.5 py-0.5 text-[10px]", mobileStage === stage.key ? "bg-white/15" : "bg-slate-100")}>{count}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-3 md:hidden">
              {(byStage.get(mobileStage) ?? []).map((lead) => (
                <PartnerLeadCard key={lead.id} lead={lead} partnerName={partner.name} onOpen={setSelectedLeadId} onStageChange={(id, stage) => void updateStage(id, stage)} updating={updatingLeadId === lead.id} />
              ))}
              {(byStage.get(mobileStage)?.length ?? 0) === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-5 py-10 text-center">
                  <CheckCircle2 className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-3 text-sm font-semibold text-slate-700">Δεν υπάρχουν leads εδώ</p>
                  <p className="mt-1 text-xs text-slate-500">Αλλάξτε στάδιο ή χρονική περίοδο.</p>
                </div>
              )}
            </div>

            <div className="hidden min-w-0 grid-cols-4 gap-3 md:grid xl:gap-4">
              {PARTNER_STAGES.map((stage) => {
                const stageLeads = byStage.get(stage.key) ?? [];
                return (
                  <div
                    key={stage.key}
                    onDragOver={(event) => { event.preventDefault(); setDragOverStage(stage.key); }}
                    onDragLeave={() => setDragOverStage(null)}
                    onDrop={() => handleDrop(stage.key)}
                    className={cn(
                      "min-w-0 rounded-2xl border p-2.5 transition-colors xl:p-3",
                      stage.panelClass,
                      dragOverStage === stage.key && "border-sky-400 bg-sky-50",
                    )}
                  >
                    <div className="mb-3 flex items-center justify-between px-1 py-1">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", stage.dotClass)} />
                        <h3 className="truncate text-xs font-bold text-slate-800 xl:text-sm">{stage.label}</h3>
                      </div>
                      <span className="rounded-full bg-white px-2 py-0.5 text-xs font-bold text-slate-600">{stageLeads.length}</span>
                    </div>
                    <div className="space-y-3">
                      {stageLeads.map((lead) => (
                        <div key={lead.id} draggable={updatingLeadId !== lead.id} onDragStart={() => setDraggedLeadId(lead.id)} onDragEnd={() => { setDraggedLeadId(null); setDragOverStage(null); }} className={cn(draggedLeadId === lead.id && "opacity-40")}>
                          <PartnerLeadCard lead={lead} partnerName={partner.name} onOpen={setSelectedLeadId} onStageChange={(id, nextStage) => void updateStage(id, nextStage)} updating={updatingLeadId === lead.id} compact />
                        </div>
                      ))}
                      {stageLeads.length === 0 && (
                        <div className="rounded-xl border border-dashed border-slate-300 bg-white/70 px-3 py-8 text-center text-xs text-slate-500">Σύρετε ένα lead εδώ</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default PartnerPipelinePage;
